import { useState, useEffect, useCallback } from "react";
import { createOrder, getAccounts, getTargetMarkets} from "../../service/api/Order";
import useSelectedDomain from "../../service/Hooks/useSelectedDomain";
import { toast } from "sonner";
import { getAccountBySecurity, getSecuritiesByDomain, getSecuritiesByDomains, getSecuritiesByDomainMonetary } from "../../service/api/SecurityService";
import { getRfiCompleted } from "../../service/api/RfiService";
import { getDomains } from "../../service/api/domainService";
import { useUserData } from "../../service/api/useUserData";
import SearchableSelect from '../../SearchableSelect';
import { useLoader } from "../../service/Hooks/useLoader";

// Sous-composant pour la sélection du domaine (admin vs manager)
const DomainSelector = ({ role, managerDomain, domains, value, onChange, error }) => {
  if (role === "manager" && managerDomain) {
    return (
      <div className="mb-3">
        <label htmlFor="domainId" className="form-label fs-4">Domaine :</label>
        <input
          type="text"
          className="form-control form-control-sm"
          value={managerDomain.name}
          readOnly
        />
      </div>
    );
  }

  return (
    <div className="mb-3">
      <label htmlFor="domainId" className="form-label fs-4">Domaine :</label>
      <SearchableSelect
        options={domains}
        value={value}
        onChange={onChange}
        labelKey="name"
        valueKey="id"
        searchKeys={["name"]}
        className={`form-select form-control-sm fs-4 ${error ? "is-invalid" : ""}`}
      />
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
};

// Sous-composant pour la sélection du titre (security)
const SecuritySelector = ({ filteredSecurities, value, onChange, error }) => (
  <div className="mb-3">
    <label htmlFor="SecurityId" className="form-label fs-4">Titre financier :</label>
    <SearchableSelect
      options={filteredSecurities}
      value={value}
      onChange={onChange}
      labelKey="name"
      valueKey="id"
      searchKeys={["name"]}
      className={`form-select form-control-sm fs-4 ${error ? "is-invalid" : ""}`}
    />
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

// Sous-composant pour la sélection RFI (conditionnel pour Monetary)
const RfiSelector = ({ rfis, value, onChange, error, isVisible }) => {
  if (!isVisible) return null;
  return (
    <div className="mb-3">
      <label htmlFor="RfiId" className="form-label mt-3 fs-4">RFI (Indication d'intérêt)</label>
      <SearchableSelect
        options={rfis}
        value={value}
        onChange={onChange}
        labelKey="securityName"
        valueKey="id"
        searchKeys={["securityName"]}
        className={`form-select form-control-sm fs-4 ${error ? "is-invalid" : ""}`}
      />
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
};

// Sous-composant pour la sélection du marché cible
const TargetMarketSelector = ({ targetMarkets, value, onChange, error }) => {
  const marketOptions = targetMarkets
    .filter(market => market.name !== "Private" && market.name !== "Non coté")
    .map((market) => ({
      value: market.id,
      label: market.name === "Public" ? "Coté" : market.name
  }));

  const handleChange = (selected) => {
    onChange({
      target: {
        name: "TargetMarketId",
        value: selected ? selected.value : ""
      }
    });
  };

  const selectedOption = marketOptions.find(option => option.value === value) || null;

  return (
    <div className="mb-4">
      <label className="form-label fs-4 fw-semibold">
        Marché cible :
      </label>
      
      <SearchableSelect
        options={marketOptions}
        value={selectedOption}
        onChange={handleChange}
        labelKey="label"
        valueKey="value"
        searchKeys={["label"]}
        className={error ? "is-invalid" : ""}
        isClearable={true}
      />
      
      {error && <div className="invalid-feedback d-block mt-1">{error}</div>}

      <div className="form-text mt-2">
        <small className="text-muted">
          • <strong>Coté</strong> : Marché public ouvert à tous les investisseurs<br/>
          • <strong>Monétaire</strong> : Titres de court terme avec RFI obligatoire
        </small>
      </div>
    </div>
  );
};

// Sous-composant pour l'upload de preuve
const ProofUploader = ({ formData, onChange, error }) => {
  const isRequired = formData.Side === "Souscription";
  
  return (
    <div className="mb-4">
      <label htmlFor="Preuve" className="form-label mt-2">
        Document justificatif
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <input
        type="file"
        id="Preuve"
        name="Preuve"
        className={`form-control ${error ? "is-invalid" : ''}`}
        onChange={onChange}
        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
      />
      
      <div className="form-text">
        <small className="text-muted">
          {isRequired 
            ? "📋 Document obligatoire pour les souscriptions (contrat, bulletin de souscription...)"
            : "📎 Document justificatif (contrat, accord, preuve de transaction...)"
          }
        </small>
        <br />
        <small className="text-muted">📄 Formats acceptés: PDF, JPG, PNG, DOC (max 10MB)</small>
        
        {isRequired && (
          <>
            <br />
            <small className="text-warning fw-bold">
              ⚠️ Sans document justificatif, la souscription ne pourra pas être validée
            </small>
          </>
        )}
      </div>
      
      {error && (
        <div className="invalid-feedback d-block">
          <i className="fas fa-exclamation-triangle me-1"></i>
          {error}
        </div>
      )}
    </div>
  );
};

// Type ordre, Prix, Quantité
const OrderDetails = ({ formData, errors, onChange }) => (
  <>
     <div className="mb-3">
      <label htmlFor="side" className="form-label fs-5">Nature de l'ordre :</label>
      <SearchableSelect
        options={[
          { id: "Buy", name: "Achat" },
          { id: "Souscription", name: "Souscription" }
        ]}
        value={formData.Side}
        onChange={(selected) => onChange({ target: { name: "Side", value: selected?.id || "" } })}
        labelKey="name"
        valueKey="id"
        className={`form-select form-control-sm fs-4 ${errors.Side ? "is-invalid" : ""}`}
      />
      {errors.Side && <div className="invalid-feedback">{errors.Side}</div>}
      
      {formData.Side === "Souscription" && (
        <div className="form-text text-info">
          <i className="bi bi-info-circle me-1"></i>
          La souscription permet d'acquérir de nouveaux titres lors d'émissions primaires
        </div>
      )}
    </div>

    <div className="mb-3">
      <label htmlFor="price" className="form-label fs-5">Prix unitaire</label>
      <input
        type="number"
        id="Price"
        name="Price"
        className={`form-control ${errors.Price ? "is-invalid" : ""}`}
        value={formData.Price}
        onChange={onChange}
      />
      {errors.Price && <div className="invalid-feedback">{errors.Price}</div>}
    </div>

    <div className="mb-3">
      <label htmlFor="quantity" className="form-label fs-5">Quantité</label>
      <input
        type="number"
        id="Quantity"
        name="Quantity"
        className={`form-control ${errors.Quantity ? "is-invalid" : ""}`}
        value={formData.Quantity}
        onChange={onChange}
      />
      {errors.Quantity && <div className="invalid-feedback">{errors.Quantity}</div>}
    </div>

    <div className="mb-3">
      <label htmlFor="minQuantity" className="form-label fs-5">Quantité minimale d'exécution</label>
      <input
        type="number"
        id="MinQuantity"
        name="MinQuantity"
        className={`form-control ${errors.MinQuantity ? "is-invalid" : ""}`}
        value={formData.MinQuantity}
        onChange={onChange}
      />
      <div className="form-text">
        <i className="fas fa-info-circle me-1 mt-1"></i>
        Seuil en dessous duquel l'ordre ne sera pas exécuté partiellement.
      </div>
      {errors.MinQuantity && <div className="invalid-feedback">{errors.MinQuantity}</div>}
    </div>
  </>
);

// Sélections de comptes
const AccountSelectors = ({ accounts, toAccounts, formData, errors, onChange, selectedMarketType, isLoadingAccounts }) => (
  <>
    <div className="mb-2">
      <label htmlFor="fromAccountId" className="form-label fs-4">
        Compte donneur d'ordre :
        {isLoadingAccounts && <span className="spinner-border spinner-border-sm ms-2 text-secondary" />}
      </label>
      <SearchableSelect
        options={accounts}
        value={formData.FromAccountId}
        onChange={(selected) => onChange({ target: { name: "FromAccountId", value: selected?.id || "" } })}
        labelKey={(acc) =>
          ["Entreprise", "Banques", "Assurance", "Autres Entreprises","Opc","Opcvm","Fia","Issuer"].includes(acc.accountTypeName)
            ? acc.companyName
            : `${acc.firstName} ${acc.lastName}`
        }
        valueKey="id"
        searchKeys={["companyName", "firstName", "lastName"]}
        className={`form-select form-control-sm fs-4 ${errors.FromAccountId ? "is-invalid" : ""}`}
      />
      {errors.FromAccountId && <div className="invalid-feedback">{errors.FromAccountId}</div>}
    </div>
    {selectedMarketType === "Private" && (
      <div className="mb-1">
        <label htmlFor="ToAccountId" className="form-label fs-4">Compte cédant (vendeur) :</label>
        <SearchableSelect
          options={toAccounts}
          value={formData.ToAccountId}
          onChange={(selected) => onChange({ target: { name: "ToAccountId", value: selected?.id || "" } })}
          labelKey={(acc) => `${acc.firstName || ""} ${acc.name || ""} ${acc.companyName || ""}`.trim()}
          valueKey="id"
          searchKeys={["firstName", "name", "companyName"]}
          placeholder="un compte"
          className={`form-select form-control-sm fs-4 ${errors.ToAccountId ? "is-invalid" : ""}`}
        />
        {errors.ToAccountId && <div className="invalid-feedback">{errors.ToAccountId}</div>}
      </div>
    )}
  </>
);

const OrderForm = ({ securityId, isPrivatePage = false, onClose, onSubscriptionSuccess, }) => {
  const selectedDomain = useSelectedDomain();
  const [targetMarkets, setTargetMarkets] = useState([]);
  const [allSecurities, setAllSecurities] = useState([]);
  const [filteredSecurities, setFilteredSecurities] = useState([]);
  const [rfis, setRfis] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [domains, setDomains] = useState([]);
  const { getUserInfo } = useUserData();
  const [role, setRole] = useState(null);
  const [managerDomain, setManagerDomain] = useState(null);
  const [selectedMarketType, setSelectedMarketType] = useState(null);
  const [toAccounts, setToAccounts] = useState([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [formData, setFormData] = useState({
    SecurityId: "",
    CreatorId: "",
    Side: "",
    Price: "",
    Quantity: "",
    MinQuantity: "",
    TargetMarketId: "",
    FromAccountId: "",
    ToAccountId: null,
    RfiId: null,
    Preuve: null,
    DomainId: "",
  });
  const [errors, setErrors] = useState({});
  const {showLoader, hideLoader} = useLoader();
  

  const isSubscription = Boolean(securityId);

  //  --------------------- CHARGER LE PRIX DU TITRE APRES SELECTION ------------------ //
  useEffect(() => {
    const fetchSecurityPrice = () => {
      if (!formData.SecurityId) return;
      
      const selectedSecurity = filteredSecurities.find(
        security => security.id === formData.SecurityId
      );
      
      if (selectedSecurity && selectedSecurity.lastPrice !== undefined) {
        setFormData(prev => ({
          ...prev,
          Price: selectedSecurity.lastPrice
        }));
      }
    };
    
    fetchSecurityPrice();
  }, [formData.SecurityId, filteredSecurities]);

  const handleSecurityChange = (selected) => {
    const securityId = selected?.id || "";
    
    const selectedSecurity = filteredSecurities.find(
      security => security.id === securityId
    );
    
    setFormData(prev => ({
      ...prev,
      SecurityId: securityId,
      Price: selectedSecurity?.lastPrice || ""
    }));
    
    setErrors(errs => ({ ...errs, SecurityId: null }));
  };

  const handleCloseModal = useCallback(() => {
    const modal = window.bootstrap?.Modal.getOrCreateInstance(document.getElementById("newOrder"));
    if (modal) modal.hide();

    document.body.classList.remove("modal-open");
    const backdrops = document.querySelectorAll(".modal-backdrop");
    backdrops.forEach((bd) => bd.parentNode.removeChild(bd));
    setFormData({
      SecurityId: "",
      CreatorId: "",
      Side: "",
      Price: "",
      Quantity: "",
      MinQuantity: "",
      TargetMarketId: "",
      FromAccountId: "",
      ToAccountId: null,
      RfiId: null,
      Preuve: null,
      DomainId: "",
    });
    setErrors({});
    if (typeof onClose === "function") onClose();
  }, [onClose]);

  useEffect(() => {
    async function fetchUserData() {
      const userInfo = await getUserInfo();
      if (!userInfo) return;

      const { accountId, isAdmin, selectedDomain } = userInfo;

      setFormData((prev) => ({ ...prev, CreatorId: accountId }));

      if (isAdmin) {
        setRole("admin");
      } else {
        const storedDomain = localStorage.getItem("selectedDomain");
        if (storedDomain) {
          const domainObj = JSON.parse(storedDomain);
          setRole(domainObj.role);
          setManagerDomain(domainObj);
        } else if (selectedDomain?.role) {
          setRole(selectedDomain.role);
          setManagerDomain(selectedDomain);
        } else {
          setRole("client");
        }
      }
    }
    fetchUserData();
  }, []);

  useEffect(() => {
    async function fetchDomains() {
      try {
        const data = await getDomains();
        setDomains(data);
      } catch  {
        // silent
      }
    }
    fetchDomains();
  }, []);

  useEffect(() => {
    const fetchSecurities = async () => {
      try {
        let response;
        if (selectedMarketType === "Private") {
          response = await getSecuritiesByDomains({ pageNumber: 1, pageSize: 100000 });
        } else if (selectedMarketType === "Monetary") {
          response = await getSecuritiesByDomainMonetary({ pageNumber: 1, pageSize: 100000 });
        } else {
          response = await getSecuritiesByDomain({ pageNumber: 1, pageSize: 100000 });
        }

        const items = response.items || [];
        setAllSecurities(items);

        if (securityId) {
          const cur = items.find((s) => s.id === securityId);
          setFilteredSecurities(
            cur ? [cur, ...items.filter((s) => s.id !== securityId)] : items
          );
          setFormData((fd) => ({ ...fd, SecurityId: securityId }));
        } else {
          setFilteredSecurities(items);
        }
      } catch {
        // silent
      }
    };

    fetchSecurities();
  }, [selectedMarketType, securityId]);


  // ── FIX PRINCIPAL : charger TOUS les comptes sans limite de pagination ────────
  useEffect(() => {
    if (!selectedDomain?.id) return;

    const fetchOtherData = async () => {
      try {
        setIsLoadingAccounts(true);
        const [tm, accountsData, rfi] = await Promise.all([
          getTargetMarkets(),
          // pageSize=99999 pour récupérer tous les comptes en un seul appel
          getAccounts(selectedDomain.id, { pageNumber: 1, pageSize: 99999 }),
          getRfiCompleted(),
        ]);
        setTargetMarkets(tm);
        // Compatibilité : la réponse peut être { items: [...] } ou directement un tableau
        setAccounts(accountsData.items ?? accountsData ?? []);
        setRfis(rfi);
      } catch {
        toast.error("Erreur lors du chargement des données.");
      } finally {
        setIsLoadingAccounts(false);
      }
    };

    fetchOtherData();
  }, [selectedDomain]);

  useEffect(() => {
    if (role === "manager" && managerDomain) {
      setFormData((fd) => ({ ...fd, DomainId: managerDomain.id }));
    }
  }, [role, managerDomain]);

  useEffect(() => {
    if (!securityId) return;
    const curSecurity = allSecurities.find((s) => s.id === securityId);
    setFilteredSecurities(
      curSecurity
        ? [curSecurity, ...allSecurities.filter((s) => s.id !== securityId)]
        : allSecurities
    );
    setFormData((fd) => ({ ...fd, SecurityId: securityId }));
  }, [securityId, allSecurities]);

  useEffect(() => {
    if (!formData.SecurityId || !selectedDomain?.id) {
      setToAccounts([]);
      return;
    }
    getAccountBySecurity(formData.SecurityId, selectedDomain.id)
      .then((data) => {
        setToAccounts(data);
      })
      .catch(() => {
        setToAccounts([]);
      });
  }, [formData.SecurityId, selectedDomain]);

  const checkCashAvailability = (accountId, Quantity, Price, accounts) => {
    const accountIdNumber = Number(accountId);
    const account = accounts.find((acc) => acc.id === accountIdNumber);

    if (!account) {
      toast.error("Aucun compte trouvé");
      return false;
    }

    const parsedQuantity = parseFloat(Quantity);
    const parsedPrice = parseFloat(Price);
    const requiredCash = parsedQuantity * parsedPrice;

    return account.cash >= requiredCash;
  };

  useEffect(() => {
    const { FromAccountId, Quantity, Price } = formData;

    const parsedQuantity = parseFloat(Quantity);
    const parsedPrice = parseFloat(Price);

    if (FromAccountId && parsedQuantity > 0 && parsedPrice > 0) {
      const isCashSufficient = checkCashAvailability(FromAccountId, parsedQuantity, parsedPrice, accounts);
      if (!isCashSufficient) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          FromAccountId: `Solde insuffisant. Montant requis : ${(parsedQuantity * parsedPrice).toLocaleString('fr-FR')}`,
        }));
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          FromAccountId: "",
        }));
      }
    }
  }, [formData.Quantity, formData.Price, formData.FromAccountId, accounts]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    setFormData((f) => ({
      ...f,
      [name]: files ? files[0] : ["Price", "Quantity", "MinQuantity", "SecurityId", "TargetMarketId", "FromAccountId", "DomainId"].includes(name)
        ? Number(value)
        : value,
    }));

    setErrors((errs) => ({ ...errs, [name]: null }));
  };

  const handleTargetMarketChange = async (e) => {
    const selectedMarketId = e.target.value;
    const m = targetMarkets.find((m) => String(m.id) === selectedMarketId);

    setFormData((fd) => ({
      ...fd,
      TargetMarketId: selectedMarketId,
      RfiId: null,
      ToAccountId: null,
    }));

    setSelectedMarketType(m?.name);
    if (!m) {
      return setFilteredSecurities(allSecurities);
    }

    if (m.name === "Private") {
      try {
        const data = await getSecuritiesByDomains({ pageNumber: 1, pageSize: 100000 });
        setFilteredSecurities(data.items || []);
      } catch (err) {
        console.error("Erreur lors du chargement des titres privés:", err);
      }
    } else {
      try {
        let data;
        if (m.name === "Monetary") {
          data = await getSecuritiesByDomainMonetary({ pageNumber: 1, pageSize: 100000 });
        } else {
          data = await getSecuritiesByDomain({ pageNumber: 1, pageSize: 100000 });
        }
        
        const filtered = data.items || [];
        const cur = allSecurities.find((s) => s.id === formData.SecurityId);
        setFilteredSecurities(
          cur && !filtered.some((x) => x.id === cur.id) ? [cur, ...filtered] : filtered
        );
      } catch (err) {
        console.error("Erreur lors du chargement des titres:", err);
      }
    }
  };

  const validate = () => {
    const newErrors = {};

    if (formData.Side === "Souscription" && !formData.Preuve) {
      newErrors.Preuve = "Un document justificatif est obligatoire pour les souscriptions";
    }

    if (formData.Side === "Souscription") {
      if (formData.Price === "" || formData.Price === null || formData.Price === undefined) {
        newErrors.Price = "Le prix est requis pour une souscription";
      } else if (Number(formData.Price) < 0) {
        newErrors.Price = "Le prix doit être positif ou nul pour une souscription";
      }
    } else {
      if (!formData.Price || Number(formData.Price) <= 0) {
        newErrors.Price = "Le prix unitaire doit être supérieur à zéro";
      }
    }

    if (!formData.SecurityId) newErrors.SecurityId = "Le titre financier est requis";
    if (!formData.CreatorId) newErrors.CreatorId = "L'identifiant du créateur est requis";
    if (!formData.Side) newErrors.Side = "La nature de l'ordre est requise";
    
    if (!formData.Quantity || Number(formData.Quantity) <= 0) {
      newErrors.Quantity = "La quantité doit être supérieure à zéro";
    }
    
    if (!formData.MinQuantity || Number(formData.MinQuantity) <= 0) {
      newErrors.MinQuantity = "La quantité minimale doit être supérieure à zéro";
    }
    
    if (!formData.TargetMarketId) newErrors.TargetMarketId = "Le marché cible est requis";
    if (!formData.DomainId) newErrors.DomainId = "Le domaine est requis";
    if (!formData.FromAccountId) newErrors.FromAccountId = "Le compte donneur d'ordre est requis";

    if (selectedMarketType === "Monetary" && !formData.RfiId) {
      newErrors.RfiId = "Le RFI est obligatoire pour le marché monétaire";
    }

    if (selectedMarketType === "Private" && !formData.ToAccountId) {
      newErrors.ToAccountId = "Le compte cédant est obligatoire pour le marché de gré à gré";
    }

    if (Number(formData.Quantity) < Number(formData.MinQuantity)) {
      newErrors.Quantity = "La quantité totale doit être supérieure ou égale à la quantité minimale";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const normalizeId = (val) => {
    return val !== undefined && val !== "" ? Number(val) : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error("Veuillez renseigner correctement les champs du formulaire.");
      return;
    }

    try {
      const fd = new FormData();

      Object.entries({
        ...formData,
        SecurityId: normalizeId(formData.SecurityId),
        CreatorId: normalizeId(formData.CreatorId),
        TargetMarketId: normalizeId(formData.TargetMarketId),
        FromAccountId: normalizeId(formData.FromAccountId),
        Price: parseFloat(formData.Price),
        Quantity: parseFloat(formData.Quantity),
        MinQuantity: parseFloat(formData.MinQuantity),
        DomainId: normalizeId(formData.DomainId),
        ToAccountId: normalizeId(formData.ToAccountId),
        RfiId: normalizeId(formData.RfiId),
      }).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          fd.append(key, value);
        }
      });

      if (formData.Preuve) {
        fd.append("Preuve", formData.Preuve);
      }

      const config = { headers: { "Content-Type": "multipart/form-data" } };

      showLoader({ 
        message: formData.Side === "Souscription" 
          ? "Enregistrement de la souscription en cours..." 
          : "Transmission de l'ordre en cours...", 
        indeterminate: true 
      });
      
      await createOrder(fd, config);
      
      toast.success(
        formData.Side === "Souscription" 
          ? "Souscription enregistrée avec succès !" 
          : "Ordre transmis avec succès !"
      );

      setFormData({
        SecurityId: "",
        CreatorId: "",
        Side: "",
        Price: "",
        Quantity: "",
        MinQuantity: "",
        TargetMarketId: "",
        FromAccountId: "",
        ToAccountId: null,
        RfiId: null,
        Preuve: null,
        DomainId: ""
      });
      setErrors({});
      handleCloseModal();

    } catch (error) {
      
      let errorMessage = "Erreur lors de la création";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        errorMessage = typeof error.response.data === 'string' 
          ? error.response.data 
          : JSON.stringify(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(
        formData.Side === "Souscription"
          ? `Échec de la souscription : ${errorMessage}`
          : `Échec de la transmission : ${errorMessage}`,
        { duration: 5000 }
      );
    } finally {
      hideLoader();
    }
  };

  return (
    <>
      <div className="modal fade" id="newOrder" tabIndex="-1" aria-labelledby="newOrder" aria-hidden="true">
        <div className="modal-dialog modal-lg" style={{ minWidth: "800px" }}>
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">
                {formData.Side === "Souscription" ? "Nouvelle Souscription" : "Nouvel Ordre"}
              </h3>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={handleCloseModal}></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="needs-validation" noValidate>
                <div className="container-fluid">
                  <div className="row gy-3">
                    <div className="col-md-6">
                      <fieldset>
                        <legend className="fs-9">Identification du titre</legend>
                        <DomainSelector
                          role={role}
                          managerDomain={managerDomain}
                          domains={domains}
                          value={formData.DomainId}
                          onChange={(selected) => setFormData((fd) => ({ ...fd, DomainId: selected?.id || "" }))}
                          error={errors.DomainId}
                        />
                        <SecuritySelector
                          filteredSecurities={filteredSecurities}
                          value={formData.SecurityId}
                          onChange={handleSecurityChange}
                          error={errors.SecurityId}
                        />
                        <RfiSelector
                          rfis={rfis}
                          value={formData.RfiId}
                          onChange={(selected) => setFormData((fd) => ({ ...fd, RfiId: selected?.id || "" }))}
                          error={errors.RfiId}
                          isVisible={selectedMarketType === "Monetary"}
                        />
                        <input type="hidden" name="CreatorId" value={formData.CreatorId} />
                      </fieldset>
                    </div>

                    <div className="col-md-6">
                      <fieldset>
                        <legend className="fs-9">Marché & conditions</legend>
                        <TargetMarketSelector
                          targetMarkets={targetMarkets}
                          value={formData.TargetMarketId}
                          onChange={handleTargetMarketChange}
                          error={errors.TargetMarketId}
                        />
                        
                      </fieldset>
                    </div>
                  </div>

                  <div className="row gy-5">
                    <div className="col-md-6">
                      <fieldset>
                        <legend className="fs-9">Paramètres de l'ordre</legend>
                        <OrderDetails
                          formData={formData}
                          errors={errors}
                          onChange={handleInputChange}
                        />
                      </fieldset>
                    </div>

                    <div className="col-md-6">
                      <fieldset>
                        <legend className="fs-9">Informations comptables</legend>
                        <AccountSelectors
                          accounts={accounts}
                          toAccounts={toAccounts}
                          formData={formData}
                          errors={errors}
                          onChange={handleInputChange}
                          selectedMarketType={selectedMarketType}
                          isLoadingAccounts={isLoadingAccounts}
                        />

                        <ProofUploader
                          formData={formData}
                          onChange={handleInputChange}
                          error={errors.Preuve}
                        />
                      </fieldset>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-end mt-4">
                  <button type="submit" className="btn btn-primary me-2">
                    {formData.Side === "Souscription" ? "Confirmer la souscription" : "Transmettre l'ordre"}
                  </button>
                  <button type="button" className="btn btn-danger" data-bs-dismiss="modal" onClick={handleCloseModal}>
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderForm;